import { saveUserActivity } from '../firebase/subcollections';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Study streak management utilities
export class StudyStreakManager {
  constructor(userId) {
    this.userId = userId;
    this.streakDocRef = doc(db, 'users', userId, 'streakData', 'currentStreak');
  }

  // Get current IST date (Indian Standard Time)
  getCurrentISTDate() {
    const now = new Date();
    const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    return istTime.toDateString();
  }

  // Get date string for a specific date
  getDateString(date) {
    const istTime = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istTime.toDateString();
  }

  // Check if user has activity today
  async hasActivityToday() {
    try {
      const today = this.getCurrentISTDate();
      const streakData = await this.getStreakData();
      
      return streakData.lastActivityDate === today;
    } catch (error) {
      // Error checking activity today
      return false;
    }
  }

  // Log daily activity and update streak
  async logDailyActivity(activityType = 'daily_login') {
    try {
      const today = this.getCurrentISTDate();
      const yesterday = this.getDateString(new Date(Date.now() - 24 * 60 * 60 * 1000));
      
      // Get current streak data
      const streakData = await this.getStreakData();
      
      // Check if already logged today
      if (streakData.lastActivityDate === today) {
        return { success: true, streak: streakData.currentStreak, alreadyLogged: true };
      }

      let newStreak = 1; // Default to 1 for today
      
      // If user was active yesterday, increment streak
      if (streakData.lastActivityDate === yesterday) {
        newStreak = streakData.currentStreak + 1;
      } else if (streakData.lastActivityDate !== today) {
        // If user missed a day, reset streak to 1
        newStreak = 1;
      }

      // Update streak data
      const updatedStreakData = {
        currentStreak: newStreak,
        lastActivityDate: today,
        longestStreak: Math.max(streakData.longestStreak, newStreak),
        totalActiveDays: streakData.totalActiveDays + 1,
        lastUpdated: new Date()
      };

      // Save to Firestore
      await setDoc(this.streakDocRef, updatedStreakData, { merge: true });

      // Log activity for analytics
      await saveUserActivity(this.userId, {
        type: activityType,
        title: 'Daily Activity',
        description: `Maintained ${newStreak} day study streak`,
        metadata: {
          streak: newStreak,
          date: today,
          activityType
        }
      });

      return { 
        success: true, 
        streak: newStreak, 
        alreadyLogged: false,
        data: updatedStreakData
      };
    } catch (error) {
      // Error logging daily activity
      return { success: false, error: error.message };
    }
  }

  // Get current streak data
  async getStreakData() {
    try {
      const docSnap = await getDoc(this.streakDocRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        // Initialize streak data if it doesn't exist
        const defaultData = {
          currentStreak: 0,
          longestStreak: 0,
          totalActiveDays: 0,
          lastActivityDate: null,
          lastUpdated: new Date()
        };
        
        await setDoc(this.streakDocRef, defaultData);
        return defaultData;
      }
    } catch (error) {
      // Error getting streak data
      return {
        currentStreak: 0,
        longestStreak: 0,
        totalActiveDays: 0,
        lastActivityDate: null,
        lastUpdated: new Date()
      };
    }
  }

  // Calculate study streak from activities (legacy method for backward compatibility)
  static calculateStudyStreak(activities) {
    if (!activities || activities.length === 0) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Convert to IST
    const istToday = new Date(today.getTime() + (5.5 * 60 * 60 * 1000));
    istToday.setHours(0, 0, 0, 0);
    
    let streak = 0;
    let currentDate = new Date(istToday);
    
    // Check for consecutive days of activity
    while (true) {
      const hasActivity = activities.some(activity => {
        let activityDate;
        
        if (activity.timestamp) {
          if (activity.timestamp.toDate) {
            activityDate = activity.timestamp.toDate();
          } else {
            activityDate = new Date(activity.timestamp);
          }
        } else if (activity.createdAt) {
          if (activity.createdAt.toDate) {
            activityDate = activity.createdAt.toDate();
          } else {
            activityDate = new Date(activity.createdAt);
          }
        } else if (activity.date) {
          activityDate = new Date(activity.date);
        } else {
          return false;
        }
        
        // Convert to IST
        const istActivityDate = new Date(activityDate.getTime() + (5.5 * 60 * 60 * 1000));
        istActivityDate.setHours(0, 0, 0, 0);
        
        return istActivityDate.getTime() === currentDate.getTime();
      });
      
      if (hasActivity) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }
    
    return streak;
  }

  // Reset streak (for admin purposes)
  async resetStreak() {
    try {
      const resetData = {
        currentStreak: 0,
        lastActivityDate: null,
        lastUpdated: new Date()
      };
      
      await setDoc(this.streakDocRef, resetData, { merge: true });
      
      return { success: true, data: resetData };
    } catch (error) {
      // Error resetting streak
      return { success: false, error: error.message };
    }
  }

  // Get streak statistics
  async getStreakStats() {
    try {
      const streakData = await this.getStreakData();
      const today = this.getCurrentISTDate();
      const yesterday = this.getDateString(new Date(Date.now() - 24 * 60 * 60 * 1000));
      
      const isActiveToday = streakData.lastActivityDate === today;
      const wasActiveYesterday = streakData.lastActivityDate === yesterday;
      
      // Get user profile to include name
      let userName = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', this.userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userName = userData.name || userData.displayName || userData.email?.split('@')[0] || null;
        }
      } catch (userError) {
        // Error fetching user data
      }
      
      return {
        currentStreak: streakData.currentStreak,
        longestStreak: streakData.longestStreak,
        totalActiveDays: streakData.totalActiveDays,
        isActiveToday,
        wasActiveYesterday,
        lastActivityDate: streakData.lastActivityDate,
        nextMilestone: this.getNextMilestone(streakData.currentStreak),
        userName
      };
    } catch (error) {
      // Error getting streak stats
      return {
        currentStreak: 0,
        longestStreak: 0,
        totalActiveDays: 0,
        isActiveToday: false,
        wasActiveYesterday: false,
        lastActivityDate: null,
        nextMilestone: 7,
        userName: null
      };
    }
  }

  // Get next milestone
  getNextMilestone(currentStreak) {
    const milestones = [7, 30, 60, 100, 200, 365];
    return milestones.find(milestone => milestone > currentStreak) || 365;
  }

  // Get badge for current streak
  getBadge(currentStreak) {
    if (currentStreak >= 365) return { name: 'Wizard Badge', color: 'from-purple-400 to-indigo-600' };
    if (currentStreak >= 200) return { name: 'Diamond Badge', color: 'from-cyan-400 to-blue-600' };
    if (currentStreak >= 100) return { name: 'Platinum Badge', color: 'from-gray-400 to-slate-600' };
    if (currentStreak >= 60) return { name: 'Gold Badge', color: 'from-yellow-400 to-orange-600' };
    if (currentStreak >= 30) return { name: 'Silver Badge', color: 'from-gray-300 to-gray-500' };
    if (currentStreak >= 7) return { name: 'Bronze Badge', color: 'from-amber-600 to-orange-700' };
    return { name: 'Beginner Badge', color: 'from-green-400 to-blue-500' };
  }

  // Calculate progress to next milestone
  getProgressToNextMilestone(currentStreak) {
    const nextMilestone = this.getNextMilestone(currentStreak);
    const progress = (currentStreak / nextMilestone) * 100;
    return Math.min(progress, 100);
  }

  // Check if streak is at risk (missed yesterday)
  async isStreakAtRisk() {
    try {
      const streakData = await this.getStreakData();
      const today = this.getCurrentISTDate();
      const yesterday = this.getDateString(new Date(Date.now() - 24 * 60 * 60 * 1000));
      
      // Streak is at risk if user was active before yesterday but not yesterday
      return streakData.lastActivityDate && 
             streakData.lastActivityDate !== today && 
             streakData.lastActivityDate !== yesterday;
    } catch (error) {
      // Error checking streak risk
      return false;
    }
  }
}

// Global function to log daily activity
export const logDailyActivity = async (userId, activityType = 'daily_login') => {
  const streakManager = new StudyStreakManager(userId);
  return await streakManager.logDailyActivity(activityType);
};

// Global function to get streak data
export const getStreakData = async (userId) => {
  const streakManager = new StudyStreakManager(userId);
  return await streakManager.getStreakData();
};

// Global function to get streak stats
export const getStreakStats = async (userId) => {
  const streakManager = new StudyStreakManager(userId);
  return await streakManager.getStreakStats();
};

// Global function to check if user has activity today
export const hasActivityToday = async (userId) => {
  const streakManager = new StudyStreakManager(userId);
  return await streakManager.hasActivityToday();
};
